import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { AuthenticationService } from '../services/auth.service';
import { AuthfakeauthenticationService } from '../services/authfake.service';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import * as dayjs from 'dayjs';
import { getFirebaseBackend } from '../../authUtils';
import { SKIP_AUTH } from './http-context';

// Margen de 5 minutos: si el token expira en menos de 5 min, se renueva
const TOKEN_REFRESH_MARGIN_SECONDS = 300;

@Injectable()
export class JwtInterceptor implements HttpInterceptor {

	constructor(private authenticationService: AuthenticationService, private authfackservice: AuthfakeauthenticationService, private router: Router) {}

	intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
		const publicUrls = ['auto_sign_up', 'get_lms_data', 'save_login', 'verify_master_login', 'mark_firebase_created', 'get_list_widgets', 'get_database', 'save_dashboard', 'get_user_notifications', 'save_relation', 'get_numbers_lead', 'get_feedback_avg_user', 'get_address_lead', 'get_remaining'];
		const ipProviders = ['https://api.ipify.org/?format=json', 'https://ipapi.co/json/', 'https://bigdatacloud.net/data/client-ip', 'https://us1.api-bdc.net/data/client-info'];

		if (ipProviders.some((url) => request.url.includes(url))) {
			return next.handle(request);
		}

		// Third-party endpoints (e.g. the N8N Vera webhook) opt out of the internal JWT.
		if (request.context.get(SKIP_AUTH)) {
			return next.handle(request);
		}

		if (!publicUrls.some((url) => request.url.includes(url))) {
			const user = this.authenticationService.currentUser();
			if (!user || !this.isSameDay(user.loginTime)) {
				this.authenticationService.logout();
				this.router.navigateByUrl('/auth/signin/cover');
				return next.handle(request);
			}
		}

		if (environment.defaultauth === 'firebase') {
			const currentUser = this.authenticationService.currentUser();
			if (currentUser && currentUser.jwt) {
				return from(this.getValidToken(currentUser)).pipe(
					switchMap((token: string) => {
						return next.handle(request.clone({
							setHeaders: { Authorization: `Bearer ${token}` },
						}));
					})
				);
			}
		} else {
			const currentUser = this.authfackservice.currentUserValue;
			if (currentUser && currentUser.token) {
				request = request.clone({
					setHeaders: { Authorization: `Bearer ${currentUser.token}` },
				});
			}
		}

		return next.handle(request);
	}

	/**
	 * Devuelve el token actual si sigue vigente (más de 5 min),
	 * o solicita uno nuevo a Firebase si está próximo a expirar.
	 */
	private async getValidToken(user: any): Promise<string> {
		const tokenExp = this.getTokenExp(user.jwt);
		const nowInSeconds = Math.floor(Date.now() / 1000);

		if (tokenExp && tokenExp - nowInSeconds < TOKEN_REFRESH_MARGIN_SECONDS) {
			try {
				const newToken = await getFirebaseBackend()?.refreshToken();
				if (newToken) {
					user.jwt = newToken;
					getFirebaseBackend()?.setLoggeedInUser(user);
					return newToken;
				}
			} catch {
				// Si falla el refresh, intentar con el token actual
			}
		}

		return user.jwt;
	}

	/**
	 * Decodifica el campo `exp` del payload del JWT sin verificar la firma.
	 * Solo se usa para decidir si hay que pedir un token nuevo al SDK de Firebase.
	 */
	private getTokenExp(jwt: string): number | null {
		try {
			const payload = jwt.split('.')[1];
			const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
			return typeof decoded.exp === 'number' ? decoded.exp : null;
		} catch {
			return null;
		}
	}

	isSameDay(fecha: string): boolean {
		return dayjs().isSame(dayjs(fecha), 'day');
	}

}
